import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users as UsersIcon, Shield, RefreshCw, Search, ChevronDown, Trash2, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import API_BASE_URL from '../config/api';
import { shouldSuppressRateLimit } from '../utils/httpErrors';

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [statusConfirm, setStatusConfirm] = useState({
    show: false,
    userId: null,
    userEmail: null,
    action: null,
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const filterUsers = useCallback(() => {
    let filtered = [...users];

    // Filtro per search term
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.first_name && user.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.last_name && user.last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.company_name && user.company_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filtro per ruolo
    if (selectedRole !== 'all') {
      filtered = filtered.filter(user => user.role === selectedRole);
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, selectedRole]);

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/user-management/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUsers(response.data.users);
      setFilteredUsers(response.data.users);
      toast.success(`✅ Caricati ${response.data.total} utenti`);
    } catch (error) {
      console.error('Errore caricamento utenti:', error);
      toast.error('Errore nel caricamento degli utenti');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/user-management/users/export-excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type:
          response.headers['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `utenti_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success('✅ Excel scaricato');
    } catch (error) {
      console.error('Errore export excel:', error);
      toast.error('Errore nel download Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await axios.put(
        `${API_BASE_URL}/user-management/users/${userId}/role`,
        { role_name: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`✅ ${response.data.message}`);
      
      // Aggiorna la lista utenti
      setUsers(users.map(user =>
        user.id === userId ? { ...user, role: newRole } : user
      ));
    } catch (error) {
      console.error('Errore cambio ruolo:', error);
      if (shouldSuppressRateLimit(error)) {
        return;
      }
      const errorMessage = error.response?.data?.detail || 'Errore nel cambio ruolo';
      toast.error(errorMessage);
    }
  };

  const handleToggleUserStatus = (user) => {
    setStatusConfirm({
      show: true,
      userId: user.id,
      userEmail: user.email,
      action: user.is_active ? 'disable' : 'enable',
    });
  };

  const confirmToggleStatus = async () => {
    if (!statusConfirm.userId || !statusConfirm.action) return;
    
    try {
      const token = localStorage.getItem('access_token');

      const response = await axios.post(
        `${API_BASE_URL}/user-management/users/${statusConfirm.userId}/${statusConfirm.action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`✅ ${response.data.message}`);

      setUsers(users.map(user =>
        user.id === statusConfirm.userId
          ? { ...user, is_active: statusConfirm.action === 'enable' }
          : user
      ));

      setStatusConfirm({ show: false, userId: null, userEmail: null, action: null });
    } catch (error) {
      console.error('Errore cambio stato utente:', error);
      if (shouldSuppressRateLimit(error)) {
        setStatusConfirm({ show: false, userId: null, userEmail: null, action: null });
        return;
      }
      const errorMessage = error.response?.data?.detail || 'Errore nel cambio stato utente';
      toast.error(errorMessage);
      setStatusConfirm({ show: false, userId: null, userEmail: null, action: null });
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'associati':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'guest':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return '🔴';
      case 'associati':
        return '🟡';
      case 'guest':
        return '🟢';
      default:
        return '⚪';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-red-600 mr-3" />
              <h1 className="text-xl font-semibold text-gray-900">
                Gestione Ruoli Utenti
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleExportExcel}
                disabled={exporting}
                className={[
                  "flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors",
                  exporting ? "opacity-70 cursor-not-allowed" : "hover:bg-emerald-700",
                  "bg-emerald-600 text-white",
                ].join(" ")}
              >
                <Download className="h-4 w-4" />
                <span>{exporting ? 'Esportazione...' : 'Scarica Excel'}</span>
              </button>
              <button
                onClick={loadUsers}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Ricarica</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Statistiche */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center">
                <UsersIcon className="h-8 w-8 text-blue-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Totale Utenti</p>
                  <p className="text-2xl font-bold text-gray-900">{users.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center">
                <div className="text-3xl mr-3">🔴</div>
                <div>
                  <p className="text-sm text-gray-600">Admin</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {users.filter(u => u.role === 'admin').length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center">
                <div className="text-3xl mr-3">🟡</div>
                <div>
                  <p className="text-sm text-gray-600">Associati</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {users.filter(u => u.role === 'associati').length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center">
                <div className="text-3xl mr-3">🟢</div>
                <div>
                  <p className="text-sm text-gray-600">Guest</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {users.filter(u => u.role === 'guest').length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Filtri */}
          <div className="bg-white p-4 rounded-lg shadow mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cerca per nome, email o azienda..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Filtro Ruolo */}
              <div className="relative">
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
                >
                  <option value="all">Tutti i ruoli</option>
                  <option value="admin">🔴 Admin</option>
                  <option value="associati">🟡 Associati</option>
                  <option value="guest">🟢 Guest</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Tabella Utenti */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Caricamento utenti...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-8 text-center">
                <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Nessun utente trovato</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Utente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Azienda
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Telefono
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Newsletter
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Associazione
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Iscritto il
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ruolo Attuale
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cambia Ruolo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Azioni
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                              <span className="text-primary-600 font-semibold">
                                {user.first_name?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.first_name && user.last_name
                                  ? `${user.first_name} ${user.last_name}`
                                  : 'N/D'}
                              </div>
                              <div className="text-sm text-gray-500">
                                ID: {user.id}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {user.company_name || 'N/D'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{user.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{user.mobile_phone || 'N/D'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {user.newsletter_opt_in === true ? 'SI' : user.newsletter_opt_in === false ? 'NO' : 'N/D'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {user.federappalti_membership === 'associato'
                              ? 'Associato'
                              : user.federappalti_membership === 'non_interessato'
                              ? 'Non associato'
                              : user.federappalti_membership === 'interessato_info'
                              ? 'Non associato (interessato)'
                              : 'N/D'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {user.created_at ? new Date(user.created_at).toLocaleString('it-IT') : 'N/D'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getRoleBadgeColor(user.role)}`}>
                            <span className="mr-1">{getRoleIcon(user.role)}</span>
                            {user.role?.toUpperCase() || 'N/D'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={user.role || 'guest'}
                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          >
                            <option value="guest">🟢 Guest</option>
                            <option value="associati">🟡 Associati</option>
                            <option value="admin">🔴 Admin</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleToggleUserStatus(user)}
                            className={[
                              "inline-flex items-center px-3 py-2 rounded-lg transition-colors",
                              user.is_active
                                ? "text-red-600 hover:text-red-900 hover:bg-red-50"
                                : "text-green-600 hover:text-green-900 hover:bg-green-50",
                            ].join(" ")}
                            title={user.is_active ? "Disabilita utente" : "Abilita utente"}
                          >
                            {user.is_active ? (
                              <>
                                <Trash2 className="h-4 w-4 mr-1" />
                                Disabilita
                              </>
                            ) : (
                              <>
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Abilita
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Info Footer */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">ℹ️ Informazioni sui Ruoli</h3>
            <div className="text-sm text-blue-800 space-y-1">
              <p><strong>🟢 Guest:</strong> Accesso limitato (Storico Gare + Impostazioni)</p>
              <p><strong>🟡 Associati:</strong> Accesso esteso (Storico + Scheduler + Notifiche + Impostazioni)</p>
              <p><strong>🔴 Admin:</strong> Accesso completo a tutte le funzionalità</p>
            </div>
          </div>
        </div>
      </main>

      {/* Dialog di conferma cambio stato */}
      {statusConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              ⚠️ Conferma {statusConfirm.action === 'disable' ? 'Disabilitazione' : 'Abilitazione'}
            </h3>
            <p className="text-gray-700 mb-6">
              Sei sicuro di voler {statusConfirm.action === 'disable' ? 'disabilitare' : 'abilitare'} l'utente{' '}
              <strong>{statusConfirm.userEmail}</strong>?
              <br />
              <br />
              <span className="text-sm text-gray-600">
                {statusConfirm.action === 'disable'
                  ? 'L\'utente non potrà accedere e le schedulazioni email verranno messe in pausa.'
                  : 'L\'utente potrà accedere nuovamente e le schedulazioni email verranno riattivate (se associato).'}
              </span>
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setStatusConfirm({ show: false, userId: null, userEmail: null, action: null })}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={confirmToggleStatus}
                className={[
                  "px-4 py-2 text-white rounded-lg transition-colors",
                  statusConfirm.action === 'disable' ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700",
                ].join(" ")}
              >
                <Trash2 className="h-4 w-4 inline mr-1" />
                Conferma
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;

